(function () {
  'use strict';

  var GLOBAL_KEY = '__EV_DESKTOP_NAV__';
  var DESKTOP_MQ = '(min-width: 1200px)';
  var BODY_LOCK_CLASS = 'ev-desktop-nav-open';
  var CONTRAST_CLASS = 'ev-desktop-nav-contrast-on';
  var CONTRAST_STORAGE_KEY = 'ev-desktop-nav-enhanced-contrast';
  var INERT_FLAG = 'data-ev-desktop-nav-inert';
  var KBD_FOCUS_CLASS = 'ev-desktop-nav--kbd-focus';
  var INSTANCE_KEY = '_evDesktopNavController';
  var DEFERRED_OPEN_MS = 320;
  var VIEW_TRANSITION_MS = 320;
  var VERSION = 15;

  function isDesktop() {
    return window.matchMedia(DESKTOP_MQ).matches;
  }

  function normalizeHandle(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  function getFocusableElements(container) {
    if (!container) return [];
    var selector = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    return Array.prototype.filter.call(container.querySelectorAll(selector), function (el) {
      if (el.disabled) return false;
      if (el.getAttribute('aria-hidden') === 'true') return false;
      if (el.closest('[hidden]')) return false;
      return true;
    });
  }

  function closeCompetingOverlays() {
    // Prefer existing Wpbingo close entry points so internal flags stay consistent.
    try {
      if (document.body.classList.contains('drawer--open')) {
        var cartClose = document.querySelector('.js-drawer-close');
        if (cartClose) {
          cartClose.click();
        }
      }
    } catch (error) {
      // Non-fatal.
    }

    try {
      var searchOpen =
        document.body.classList.contains('active_search') ||
        document.querySelector('.content-search-toggle.active');
      if (searchOpen) {
        var searchClose = document.querySelector('[data-close-search-toggle]');
        if (searchClose) {
          searchClose.click();
        }
      }
    } catch (error) {
      // Non-fatal.
    }
  }

  function competingOverlayStillOpen() {
    return (
      document.body.classList.contains('drawer--open') ||
      document.body.classList.contains('active_search') ||
      !!document.querySelector('.content-search-toggle.active')
    );
  }

  function readContrastPreference() {
    try {
      return window.localStorage.getItem(CONTRAST_STORAGE_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function writeContrastPreference(enabled) {
    try {
      window.localStorage.setItem(CONTRAST_STORAGE_KEY, enabled ? '1' : '0');
    } catch (error) {
      // Persistence is optional.
    }
  }

  function applyContrastClass(enabled) {
    document.documentElement.classList.toggle(CONTRAST_CLASS, !!enabled);
  }

  function syncContrastControls(enabled) {
    var controls = document.querySelectorAll('[data-ev-desktop-nav-contrast]');
    Array.prototype.forEach.call(controls, function (control) {
      control.setAttribute('aria-checked', enabled ? 'true' : 'false');
      control.classList.toggle('is-checked', !!enabled);
    });
  }

  function createController(wrapper) {
    var openButton = wrapper.querySelector('[data-ev-desktop-nav-open]');
    var root = wrapper.querySelector('[data-ev-desktop-nav-root]');
    var dialog = wrapper.querySelector('[data-ev-desktop-nav-dialog]');
    var shell = wrapper.querySelector('[data-ev-desktop-nav-shell]');
    var rootView = wrapper.querySelector('[data-ev-desktop-nav-view-root]');
    var rootNav = wrapper.querySelector('.ev-desktop-nav__primary-nav');
    var overlay = wrapper.querySelector('[data-ev-desktop-nav-overlay]');
    var closeButtons = wrapper.querySelectorAll('[data-ev-desktop-nav-close]');
    var departmentButtons = wrapper.querySelectorAll('[data-ev-desktop-nav-department]');
    var departmentViews = wrapper.querySelectorAll('[data-ev-desktop-nav-view-department]');
    var backButtons = wrapper.querySelectorAll('[data-ev-desktop-nav-back]');
    var contrastControls = wrapper.querySelectorAll('[data-ev-desktop-nav-contrast]');
    var mediaQuery = window.matchMedia(DESKTOP_MQ);
    var lastFocus = null;
    var openIntent = 'pointer';
    var openIntentFromInput = false;
    var localAbort = null;
    var isOpen = false;
    var inertedElements = [];
    var listeners = [];
    var pendingOpenTimer = null;
    var pendingOpenGeneration = 0;
    var activeDepartmentHandle = null;
    var lastDepartmentTrigger = null;
    var rootScrollTop = 0;
    var viewTransitionTimer = null;
    var viewTransitionGeneration = 0;

    if (!openButton || !root || !dialog) {
      return {
        destroy: function () {},
        close: function () {},
        cancelPendingOpen: function () {},
        isOpen: function () {
          return false;
        }
      };
    }

    if (!dialog.hasAttribute('tabindex')) {
      dialog.setAttribute('tabindex', '-1');
    }

    function listen(target, eventName, handler, options) {
      if (!target || typeof target.addEventListener !== 'function') return;
      var capture = false;
      if (typeof options === 'boolean') {
        capture = options;
      } else if (options && typeof options === 'object') {
        capture = !!options.capture;
      }
      target.addEventListener(eventName, handler, options);
      listeners.push({
        kind: 'dom',
        target: target,
        eventName: eventName,
        handler: handler,
        capture: capture
      });
    }

    function listenMedia(mq, handler) {
      if (!mq) return;
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', handler);
        listeners.push({
          kind: 'mq-modern',
          target: mq,
          eventName: 'change',
          handler: handler
        });
      } else if (typeof mq.addListener === 'function') {
        mq.addListener(handler);
        listeners.push({
          kind: 'mq-legacy',
          target: mq,
          handler: handler
        });
      }
    }

    function cancelPendingOpen() {
      if (pendingOpenTimer !== null) {
        window.clearTimeout(pendingOpenTimer);
        pendingOpenTimer = null;
      }
      pendingOpenGeneration += 1;
    }

    function clearOurInert() {
      Array.prototype.forEach.call(inertedElements, function (el) {
        if (el && el.hasAttribute && el.hasAttribute(INERT_FLAG)) {
          el.removeAttribute('inert');
          el.removeAttribute(INERT_FLAG);
        }
      });
      inertedElements = [];

      var leftovers = document.querySelectorAll('[' + INERT_FLAG + ']');
      Array.prototype.forEach.call(leftovers, function (el) {
        el.removeAttribute('inert');
        el.removeAttribute(INERT_FLAG);
      });
    }

    function markInert(el) {
      if (!el || el.hasAttribute('inert')) return;
      el.setAttribute('inert', '');
      el.setAttribute(INERT_FLAG, '');
      inertedElements.push(el);
    }

    function inertExcept(container, keep) {
      if (!container) return;
      Array.prototype.forEach.call(container.children, function (child) {
        if (child === keep || child.contains(keep)) {
          if (child !== keep) {
            inertExcept(child, keep);
          }
          return;
        }
        markInert(child);
      });
    }

    function applyBackgroundInert() {
      clearOurInert();
      Array.prototype.forEach.call(document.body.children, function (child) {
        if (wrapper && child.contains(wrapper)) {
          inertExcept(child, wrapper);
          return;
        }
        markInert(child);
      });
    }

    function resetClosedUiState() {
      isOpen = false;
      root.hidden = true;
      openButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove(BODY_LOCK_CLASS);
      clearOurInert();
      if (dialog) dialog.setAttribute('data-ev-nav-state', 'root');
    }

    function prefersReducedMotion() {
      return (
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
    }

    function clearViewTransition() {
      if (viewTransitionTimer !== null) {
        window.clearTimeout(viewTransitionTimer);
        viewTransitionTimer = null;
      }
      viewTransitionGeneration += 1;
      if (rootView) {
        rootView.classList.remove('is-entering', 'is-exiting');
      }
      Array.prototype.forEach.call(departmentViews, function (view) {
        var wasExiting = view.classList.contains('is-exiting');
        view.classList.remove('is-entering', 'is-exiting');
        // Cancelled exits must finish hiding — otherwise an invisible
        // department sibling keeps layout space and clips the root menu.
        if (wasExiting && !view.hidden) {
          view.hidden = true;
        }
      });
    }

    function syncDepartmentButtons(handle) {
      var normalized = normalizeHandle(handle);
      Array.prototype.forEach.call(departmentButtons, function (button) {
        var expanded =
          normalized &&
          normalizeHandle(button.getAttribute('data-department-handle')) === normalized;
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    }

    function findDepartmentView(handle) {
      var normalized = normalizeHandle(handle);
      var found = null;
      Array.prototype.forEach.call(departmentViews, function (view) {
        if (
          normalized &&
          normalizeHandle(view.getAttribute('data-department-handle')) === normalized
        ) {
          found = view;
        }
      });
      return found;
    }

    function showViewImmediate(view) {
      if (!view) return;
      view.hidden = false;
      view.classList.remove('is-exiting');
      view.classList.add('is-entering');
      if (prefersReducedMotion()) {
        view.classList.remove('is-entering');
      }
    }

    function hideViewImmediate(view) {
      if (!view) return;
      view.hidden = true;
      view.classList.remove('is-entering', 'is-exiting');
    }

    function setDepartment(handle, options) {
      var opts = options || {};
      var normalized = normalizeHandle(handle);
      var reduceMotion = prefersReducedMotion() || opts.immediate;

      // Clear first, then capture generation — capturing before clear made
      // every exit timeout stale (generation mismatch), so department views
      // never hid after Back and permanently compressed the root menu.
      clearViewTransition();
      var generation = viewTransitionGeneration;
      syncDepartmentButtons(normalized || null);

      if (!normalized) {
        activeDepartmentHandle = null;
        if (dialog) dialog.setAttribute('data-ev-nav-state', 'root');

        Array.prototype.forEach.call(departmentViews, function (view) {
          if (!view.hidden) {
            if (reduceMotion) {
              hideViewImmediate(view);
            } else {
              view.classList.add('is-exiting');
              viewTransitionTimer = window.setTimeout(function () {
                if (generation !== viewTransitionGeneration) return;
                hideViewImmediate(view);
              }, VIEW_TRANSITION_MS);
            }
          } else {
            hideViewImmediate(view);
          }
        });

        if (rootView) {
          showViewImmediate(rootView);
          if (rootNav) rootNav.scrollTop = rootScrollTop;
        }

        if (opts.focusTrigger !== false && lastDepartmentTrigger) {
          window.requestAnimationFrame(function () {
            focusElement(lastDepartmentTrigger, {
              preventScroll: true,
              focusVisible: !!opts.focusVisible
            });
          });
        }

        return null;
      }

      var targetView = findDepartmentView(normalized);
      if (!targetView) {
        return setDepartment(null, { immediate: true });
      }

      if (rootNav && !rootView.hidden) {
        rootScrollTop = rootNav.scrollTop;
      } else if (rootView && !rootView.hidden) {
        rootScrollTop = rootView.scrollTop;
      }

      activeDepartmentHandle = normalized;
      if (dialog) dialog.setAttribute('data-ev-nav-state', 'department');

      // Hide other department views immediately.
      Array.prototype.forEach.call(departmentViews, function (view) {
        if (view !== targetView) hideViewImmediate(view);
      });

      // Replace root with department in one composed step (no blank sheet).
      hideViewImmediate(rootView);
      showViewImmediate(targetView);

      var backBtn = targetView.querySelector('[data-ev-desktop-nav-back]');
      var title = targetView.querySelector('.ev-desktop-nav__department-title');
      var focusTarget = backBtn || title;
      if (focusTarget) {
        window.requestAnimationFrame(function () {
          if (!isOpen || activeDepartmentHandle !== normalized) return;
          focusElement(focusTarget, {
            preventScroll: true,
            focusVisible: openIntent === 'keyboard' || !!opts.focusVisible
          });
        });
      }

      return targetView;
    }

    function focusElement(el, options) {
      if (!el || typeof el.focus !== 'function') return;
      var opts = options || {};
      var prev = dialog
        ? dialog.querySelectorAll('.' + KBD_FOCUS_CLASS)
        : [];
      Array.prototype.forEach.call(prev, function (node) {
        node.classList.remove(KBD_FOCUS_CLASS);
      });
      if (opts.focusVisible) {
        el.classList.add(KBD_FOCUS_CLASS);
        var onBlur = function () {
          el.classList.remove(KBD_FOCUS_CLASS);
          el.removeEventListener('blur', onBlur);
        };
        el.addEventListener('blur', onBlur);
      }
      try {
        el.focus(opts);
      } catch (err) {
        try {
          el.focus({ preventScroll: !!opts.preventScroll });
        } catch (err2) {
          el.focus();
        }
      }
    }

    function close() {
      cancelPendingOpen();
      clearViewTransition();

      var wasOpen = isOpen;
      resetClosedUiState();
      setDepartment(null, { immediate: true, focusTrigger: false });
      lastDepartmentTrigger = null;
      rootScrollTop = 0;

      if (wasOpen && lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus();
      }
      lastFocus = null;
    }

    function openNow() {
      // Final open boundary: re-check competitors immediately before any open side effects.
      if (!isDesktop() || competingOverlayStillOpen()) {
        cancelPendingOpen();
        resetClosedUiState();
        setDepartment(null);
        lastFocus = null;
        return;
      }

      if (isOpen) return;

      cancelPendingOpen();
      isOpen = true;
      lastFocus = document.activeElement;
      root.hidden = false;
      openButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add(BODY_LOCK_CLASS);
      applyBackgroundInert();
      setDepartment(null, { immediate: true, focusTrigger: false });
      if (rootView) {
        showViewImmediate(rootView);
        if (rootNav) rootNav.scrollTop = 0;
      }

      // Pointer open: focus the dialog container (tabindex=-1) so Close does not
      // show an automatic focus ring. Keyboard open: focus Close with visible focus.
      var closeButton = dialog.querySelector('[data-ev-desktop-nav-close]');
      var target =
        openIntent === 'keyboard'
          ? closeButton || getFocusableElements(dialog)[0] || dialog
          : dialog;
      if (dialog && !dialog.hasAttribute('tabindex')) {
        dialog.setAttribute('tabindex', '-1');
      }
      window.requestAnimationFrame(function () {
        if (!isOpen) return;
        focusElement(target, {
          preventScroll: true,
          focusVisible: openIntent === 'keyboard',
        });
      });
    }

    function scheduleDeferredOpen() {
      cancelPendingOpen();
      var generation = pendingOpenGeneration;
      pendingOpenTimer = window.setTimeout(function () {
        pendingOpenTimer = null;
        if (generation !== pendingOpenGeneration) return;
        openNow();
      }, DEFERRED_OPEN_MS);
    }

    function open() {
      if (!isDesktop()) return;

      closeCompetingOverlays();
      if (competingOverlayStillOpen()) {
        scheduleDeferredOpen();
        return;
      }

      openNow();
    }

    function onTriggerPointerDown() {
      openIntent = 'pointer';
      openIntentFromInput = true;
    }

    function onTriggerKeyDown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        openIntent = 'keyboard';
        openIntentFromInput = true;
      }
    }

    function onTriggerClick(event) {
      event.preventDefault();
      // Prefer pointerdown/keydown modality. Do not let click.detail override
      // pointerdown — some click paths (including automation) report detail 0.
      if (!openIntentFromInput) {
        openIntent = event.detail === 0 ? 'keyboard' : 'pointer';
      }
      openIntentFromInput = false;
      if (isOpen) {
        close();
        return;
      }
      // A second click while a deferred open is pending cancels it (one pending max).
      if (pendingOpenTimer !== null) {
        cancelPendingOpen();
        resetClosedUiState();
        return;
      }
      open();
    }

    function onCloseClick(event) {
      event.preventDefault();
      close();
    }

    function onOverlayClick(event) {
      event.preventDefault();
      close();
    }

    function onDepartmentClick(event) {
      event.preventDefault();
      var button = event.currentTarget;
      var handle = button.getAttribute('data-department-handle');
      lastDepartmentTrigger = button;
      // Always enter department state (replacement). Toggle-closed is via Back.
      setDepartment(handle, {
        focusVisible: openIntent === 'keyboard' || event.detail === 0
      });
    }

    function onBackClick(event) {
      event.preventDefault();
      setDepartment(null, {
        focusVisible: openIntent === 'keyboard' || event.detail === 0
      });
    }

    function onKeydown(event) {
      if (!isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      var focusables = getFocusableElements(dialog);
      if (!focusables.length) {
        event.preventDefault();
        focusElement(dialog, { preventScroll: true });
        return;
      }

      // When the dialog container holds initial pointer-open focus, Tab enters the trap.
      if (document.activeElement === dialog) {
        event.preventDefault();
        if (event.shiftKey) {
          focusElement(focusables[focusables.length - 1], {
            preventScroll: true,
            focusVisible: true,
          });
        } else {
          focusElement(focusables[0], { preventScroll: true, focusVisible: true });
        }
        return;
      }

      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        focusElement(last, { preventScroll: true, focusVisible: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        focusElement(first, { preventScroll: true, focusVisible: true });
      }
    }

    function onMediaChange() {
      if (!mediaQuery.matches) {
        cancelPendingOpen();
        if (isOpen) {
          close();
        } else {
          resetClosedUiState();
        }
      }
    }

    function onContrastToggle(event) {
      event.preventDefault();
      var enabled = !document.documentElement.classList.contains(CONTRAST_CLASS);
      applyContrastClass(enabled);
      writeContrastPreference(enabled);
      syncContrastControls(enabled);
    }

    function unbind() {
      cancelPendingOpen();

      Array.prototype.forEach.call(listeners, function (entry) {
        try {
          if (entry.kind === 'mq-legacy') {
            if (entry.target && typeof entry.target.removeListener === 'function') {
              entry.target.removeListener(entry.handler);
            }
          } else if (entry.kind === 'mq-modern') {
            if (entry.target && typeof entry.target.removeEventListener === 'function') {
              entry.target.removeEventListener(entry.eventName, entry.handler);
            }
          } else if (entry.target && typeof entry.target.removeEventListener === 'function') {
            entry.target.removeEventListener(entry.eventName, entry.handler, entry.capture);
          }
        } catch (error) {
          // Best-effort cleanup.
        }
      });
      listeners = [];

      if (localAbort) {
        try {
          localAbort.abort();
        } catch (error) {
          // ignore
        }
        localAbort = null;
      }
    }

    function bind() {
      unbind();
      localAbort = typeof AbortController === 'function' ? new AbortController() : null;
      var signal = localAbort ? localAbort.signal : undefined;
      var opts = signal ? { signal: signal } : undefined;

      listen(openButton, 'pointerdown', onTriggerPointerDown, opts);
      listen(openButton, 'keydown', onTriggerKeyDown, opts);
      listen(openButton, 'click', onTriggerClick, opts);

      Array.prototype.forEach.call(closeButtons, function (button) {
        listen(button, 'click', onCloseClick, opts);
      });

      if (overlay) {
        listen(overlay, 'click', onOverlayClick, opts);
      }

      Array.prototype.forEach.call(departmentButtons, function (button) {
        listen(button, 'click', onDepartmentClick, opts);
      });

      Array.prototype.forEach.call(backButtons, function (button) {
        listen(button, 'click', onBackClick, opts);
      });

      Array.prototype.forEach.call(contrastControls, function (control) {
        listen(control, 'click', onContrastToggle, opts);
      });

      // Localization forms: reuse the same disclosure input/option contract as
      // the theme locale selector, scoped to this drawer only (no global handler).
      Array.prototype.forEach.call(
        wrapper.querySelectorAll('[data-ev-desktop-nav-disclosure]'),
        function (disclosure) {
          var input = disclosure.querySelector('[data-disclosure-input]');
          var toggle = disclosure.querySelector('[data-ev-desktop-nav-disclosure-toggle]');
          var panel = disclosure.querySelector('[data-ev-desktop-nav-disclosure-panel]');
          var options = disclosure.querySelectorAll('[data-disclosure-option]');

          if (toggle && panel) {
            listen(
              toggle,
              'click',
              function (evt) {
                evt.preventDefault();
                var open = toggle.getAttribute('aria-expanded') === 'true';
                toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
                if (open) {
                  panel.setAttribute('hidden', '');
                } else {
                  panel.removeAttribute('hidden');
                }
              },
              opts
            );
          }

          Array.prototype.forEach.call(options, function (option) {
            listen(
              option,
              'click',
              function (evt) {
                evt.preventDefault();
                if (!input) return;
                input.value = option.getAttribute('data-value') || '';
                var form = disclosure.closest('form');
                if (form) form.submit();
              },
              opts
            );
          });
        }
      );

      listen(document, 'keydown', onKeydown, opts);
      listenMedia(mediaQuery, onMediaChange);

      var contrastEnabled = readContrastPreference();
      applyContrastClass(contrastEnabled);
      syncContrastControls(contrastEnabled);
    }

    function destroy() {
      cancelPendingOpen();
      close();
      unbind();
    }

    bind();

    return {
      destroy: destroy,
      close: close,
      cancelPendingOpen: cancelPendingOpen,
      isOpen: function () {
        return isOpen;
      },
      hasPendingOpen: function () {
        return pendingOpenTimer !== null;
      }
    };
  }

  function destroyWrapper(wrapper) {
    if (!wrapper) return;
    if (wrapper[INSTANCE_KEY] && typeof wrapper[INSTANCE_KEY].destroy === 'function') {
      wrapper[INSTANCE_KEY].destroy();
    }
    wrapper[INSTANCE_KEY] = null;
  }

  function initWrapper(wrapper) {
    if (!wrapper) return;
    destroyWrapper(wrapper);
    wrapper[INSTANCE_KEY] = createController(wrapper);
  }

  function initAll(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var wrappers = root.querySelectorAll('[data-ev-desktop-nav]');
    Array.prototype.forEach.call(wrappers, initWrapper);
  }

  function onSectionLoad(event) {
    if (!event || !event.target || !event.target.querySelector) return;
    if (event.target.querySelector('[data-ev-desktop-nav]')) {
      initAll(event.target);
    }
  }

  function onSectionUnload(event) {
    if (!event || !event.target || !event.target.querySelectorAll) return;
    var wrappers = event.target.querySelectorAll('[data-ev-desktop-nav]');
    Array.prototype.forEach.call(wrappers, destroyWrapper);
  }

  function bootstrap() {
    var existing = window[GLOBAL_KEY];
    if (existing && existing.version === VERSION && existing.bootstrapped) {
      existing.initAll(document);
      return existing;
    }

    if (existing && typeof existing.teardownGlobals === 'function') {
      existing.teardownGlobals();
    }

    var api = {
      version: VERSION,
      bootstrapped: true,
      initAll: initAll,
      teardownGlobals: function () {
        document.removeEventListener('shopify:section:load', onSectionLoad);
        document.removeEventListener('shopify:section:unload', onSectionUnload);
        api.bootstrapped = false;
      }
    };

    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('shopify:section:unload', onSectionUnload);

    window[GLOBAL_KEY] = api;

    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        function onReady() {
          document.removeEventListener('DOMContentLoaded', onReady);
          initAll(document);
        }
      );
    } else {
      initAll(document);
    }

    return api;
  }

  bootstrap();
})();
